import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Props for ProtectedRoute component
 */
interface ProtectedRouteProps {
	/** Child components to render when authenticated */
	children: ReactNode;
	/** Optional roles required to access this route */
	requiredRoles?: string[];
	/** Custom fallback component for unauthorized access */
	fallback?: ReactNode;
}

/**
 * Protected route component
 *
 * @description Wraps components that require authentication. Automatically
 * redirects unauthenticated users to the login page with a return URL.
 * Supports role-based access control for additional security.
 *
 * Features:
 * - Automatic redirect to login with return URL
 * - Role-based access control
 * - Loading state handling
 * - Custom fallback components
 * - Preserves intended navigation path
 *
 * @example
 * ```tsx
 * // Basic protection
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 *
 * // Role-based protection
 * <ProtectedRoute requiredRoles={['ADMIN', 'WORKFLOW_MANAGER']}>
 *   <AdminPanel />
 * </ProtectedRoute>
 * ```
 */
export function ProtectedRoute({ children, requiredRoles = [], fallback }: ProtectedRouteProps) {
	const { isAuthenticated, user, isLoading } = useAuth();
	const location = useLocation();

	// Show loading spinner while authentication is being initialized
	if (isLoading) {
		return (
			<div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900">
				<div className="text-center">
					<div className="mx-auto w-12 h-12 rounded-full border-b-2 border-blue-600 animate-spin"></div>
					<p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
				</div>
			</div>
		);
	}

	// Redirect to login if not authenticated
	if (!isAuthenticated) {
		const redirectUrl = `${location.pathname}${location.search}`;
		return <Navigate to={`/login?redirect=${encodeURIComponent(redirectUrl)}`} replace />;
	}

	// Check role-based access if required roles are specified
	if (requiredRoles.length > 0 && user) {
		const hasRequiredRole = requiredRoles.some(role => user.roles.includes(role));

		if (!hasRequiredRole) {
			// Show custom fallback or default unauthorized message
			if (fallback) {
				return <>{fallback}</>;
			}

			return (
				<div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900">
					<div className="w-full max-w-md text-center">
						<div className="p-8 bg-white rounded-lg border border-gray-200 shadow-lg dark:bg-gray-800 dark:border-gray-700">
							<div className="flex justify-center items-center mx-auto w-16 h-16 bg-red-100 rounded-full dark:bg-red-900/20">
								<svg
									className="w-8 h-8 text-red-600 dark:text-red-400"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
									/>
								</svg>
							</div>

							<h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
								Access Denied
							</h3>

							<p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
								You don't have permission to access this page. Required roles:{' '}
								{requiredRoles.join(', ')}
							</p>

							<p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
								Your roles: {user.roles.join(', ')}
							</p>

							<button
								onClick={() => window.history.back()}
								className="inline-flex items-center px-4 py-2 mt-6 text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
							>
								Go Back
							</button>
						</div>
					</div>
				</div>
			);
		}
	}

	// Render protected content
	return <>{children}</>;
}
